import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Función helper para parsear fechas sin problemas de zona horaria
// Cuando la fecha viene como "2024-01-15" o "2024-01-15T00:00:00.000Z"
// esto asegura que se muestre la fecha correcta sin restar un día
const parseLocalDate = (dateString) => {
  if (!dateString) return new Date();
  
  // Si es un string ISO con T, extraer solo la parte de la fecha
  const dateOnly = dateString.split('T')[0];
  
  // Parsear como partes separadas para evitar ajuste de zona horaria
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  // Crear fecha con componentes locales (mes es 0-indexado en JS)
  return new Date(year, month - 1, day);
};

// Función para formatear fecha a formato venezolano (dd/mm/yyyy)
const formatDateVE = (dateString) => {
  const date = parseLocalDate(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Función principal que puede descargar o devolver para preview
export const generateRetentionPDF = (retention, provider, empresa, options = {}) => {
  const { preview = false } = options;
  
  if (retention.tipo === 'ISLR') {
    return generateISLRPDF(retention, provider, empresa, preview);
  }
  return generateIVAPDF(retention, provider, empresa, preview);
};

// Función para abrir vista previa en nueva ventana
export const previewRetentionPDF = (retention, provider, empresa) => {
  const doc = generateRetentionPDF(retention, provider, empresa, { preview: true });
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  // Abrir en nueva ventana para imprimir
  const printWindow = window.open(pdfUrl, '_blank');
  
  // Retornar una promesa que se resuelve cuando se imprime
  return new Promise((resolve) => {
    if (printWindow) {
      printWindow.onload = () => {
        // Esperar un momento para que cargue el PDF
        setTimeout(() => {
          printWindow.print();
          resolve(true);
        }, 500);
      };
    } else {
      resolve(false);
    }
  });
};

// ============= GENERADOR IVA =============
const generateIVAPDF = (retention, provider, empresa, preview = false) => {
  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'letter'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const black = [0, 0, 0];
    const headerBg = [220, 220, 220];
    
    // ============= TÍTULO PRINCIPAL =============
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('COMPROBANTE DE RETENCION DE I.V.A.', pageWidth / 2, 12, { align: 'center' });
    
    let y = 18;
    
    // ============= FILA 1: Nro Comprobante, Fecha, Legal, RIF, Periodo =============
    // Caja Nro. Comprobante
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Nro. Comprobante', margin + 1, y);
    doc.rect(margin, y + 1, 32, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(retention.numero_comprobante || '', margin + 1, y + 6);
    
    // Caja Fecha
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Fecha:', margin + 35, y);
    doc.rect(margin + 33, y + 1, 22, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const fechaEmision = formatDateVE(retention.fecha);
    doc.text(fechaEmision, margin + 35, y + 6);
    
    // Caja Ley IVA Art. 11
    const legalX = margin + 58;
    const legalWidth = 100;
    doc.rect(legalX, y + 1, legalWidth, 8);
    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    const legalText = 'Ley IVA Art. 11 "La Administración tributaria podrá designar como responsables del pago del impuesto, en calidad de agentes de retención, a quienes por sus funciones públicas o por razón de sus actividades privadas intervengan en operaciones gravadas..."';
    const splitLegal = doc.splitTextToSize(legalText, legalWidth - 2);
    doc.text(splitLegal, legalX + 1, y + 3.5);
    
    // RIF Agente
    const rifX = legalX + legalWidth + 3;
    doc.setFontSize(6);
    doc.text('Rif Agente de Retención:', rifX + 1, y);
    doc.rect(rifX, y + 1, 30, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(empresa?.rif || '', rifX + 1, y + 6);
    
    // Periodo Fiscal
    const periodoX = rifX + 33;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Periodo Fiscal:', periodoX + 1, y);
    doc.rect(periodoX, y + 1, 32, 8);
    const fechaObj = parseLocalDate(retention.fecha);
    doc.setFontSize(7);
    doc.text(`Año: ${fechaObj.getFullYear()}  Mes: ${String(fechaObj.getMonth() + 1).padStart(2, '0')}`, periodoX + 1, y + 6);
    
    // ============= FILA 2: Nombre Agente =============
    y += 12;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Nombre o Razón Social del Agente de Retención:', margin + 1, y);
    doc.rect(margin, y + 1, pageWidth - margin * 2, 7);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(empresa?.nombre || '', margin + 1, y + 5);
    
    // ============= FILA 3: Dirección Agente =============
    y += 10;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Dirección Fiscal del Agente de Retención:', margin + 1, y);
    doc.rect(margin, y + 1, pageWidth - margin * 2, 7);
    doc.setFontSize(8);
    doc.text(empresa?.direccion || '', margin + 1, y + 5);
    
    // ============= FILA 4: Nombre y RIF Sujeto =============
    y += 10;
    const sujetoWidth = (pageWidth - margin * 2) * 0.65;
    const rifSujetoWidth = (pageWidth - margin * 2) * 0.35;
    
    doc.setFontSize(6);
    doc.text('Nombre o Razón Social del Sujeto Retenido:', margin + 1, y);
    doc.rect(margin, y + 1, sujetoWidth, 7);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(provider?.nombre || '', margin + 1, y + 5);
    
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Rif Sujeto Retenido:', margin + sujetoWidth + 1, y);
    doc.rect(margin + sujetoWidth, y + 1, rifSujetoWidth, 7);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(provider?.rif || '', margin + sujetoWidth + 1, y + 5);
    
    // ============= FILA 5: Dirección Sujeto =============
    y += 10;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Dirección Fiscal del Sujeto Retenido:', margin + 1, y);
    doc.rect(margin, y + 1, pageWidth - margin * 2, 7);
    doc.setFontSize(8);
    doc.text(provider?.direccion || '', margin + 1, y + 5);
    
    // ============= TABLA IVA =============
    y += 12;
    
    const tableData = [[
      '1',
      formatDateVE(retention.fecha_factura),
      retention.numero_factura || '',
      retention.numero_control || '', // Nº Control
      '0', // Nota Crédito
      '0', // Nota Débito
      formatCurrency(retention.monto_total_factura),
      '0,00',
      formatCurrency(retention.subtotal),
      '16%',
      `${retention.porcentaje_retencion}%`,
      formatCurrency(retention.iva_factura),
      formatCurrency(retention.monto_retenido)
    ]];
    
    autoTable(doc, {
      startY: y,
      head: [['Oper', 'Fecha\nDoc', 'Nº\nFactura', 'Nº\nControl', 'Nota\nCrédito', 'Nota\nDébito', 'Total\nCompras', 'Sin Der.\nIVA', 'Base\nImponible', '%\nAlic.', '%\nRet.', 'Impuesto\nIVA', 'IVA\nRetenido']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 6, cellPadding: 1, lineColor: black, lineWidth: 0.1 },
      headStyles: { fillColor: headerBg, textColor: black, fontStyle: 'bold', halign: 'center', fontSize: 5 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 18, halign: 'center' },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 14, halign: 'center' },
        5: { cellWidth: 14, halign: 'center' },
        6: { cellWidth: 22, halign: 'right' },
        7: { cellWidth: 18, halign: 'right' },
        8: { cellWidth: 22, halign: 'right' },
        9: { cellWidth: 12, halign: 'center' },
        10: { cellWidth: 12, halign: 'center' },
        11: { cellWidth: 20, halign: 'right' },
        12: { cellWidth: 20, halign: 'right', fontStyle: 'bold' },
      },
    });
    
    // ============= TOTAL =============
    const finalY = doc.lastAutoTable?.finalY || y + 20;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL RETENIDO:', pageWidth - margin - 55, finalY + 6);
    doc.text(formatCurrency(retention.monto_retenido), pageWidth - margin - 5, finalY + 6, { align: 'right' });
    doc.line(pageWidth - margin - 60, finalY + 8, pageWidth - margin, finalY + 8);
    
    // ============= FIRMAS =============
    const firmaY = finalY + 22;
    const firmaWidth = (pageWidth - margin * 2) / 3;
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.line(margin, firmaY, margin + firmaWidth - 10, firmaY);
    doc.text('Firma Agente de Retención', margin + (firmaWidth - 10) / 2, firmaY + 4, { align: 'center' });
    
    doc.line(margin + firmaWidth, firmaY, margin + firmaWidth * 2 - 10, firmaY);
    doc.text('Firma Sujeto Retenido', margin + firmaWidth + (firmaWidth - 10) / 2, firmaY + 4, { align: 'center' });
    
    doc.line(margin + firmaWidth * 2, firmaY, pageWidth - margin, firmaY);
    doc.text('Fecha de Entrega', margin + firmaWidth * 2 + (firmaWidth) / 2, firmaY + 4, { align: 'center' });
    
    // Si es preview, devolver el documento sin guardar
    if (preview) {
      return doc;
    }
    
    // Guardar
    const filename = `Retencion_IVA_${retention.numero_comprobante}_${provider?.nombre?.replace(/[^a-zA-Z0-9]/g, '_') || 'Sin_Proveedor'}.pdf`;
    doc.save(filename);
    return doc;
    
  } catch (error) {
    console.error('Error generando PDF IVA:', error);
    throw new Error('Error al generar el PDF: ' + error.message);
  }
};

// ============= GENERADOR ISLR - DECRETO 1808 =============
const generateISLRPDF = (retention, provider, empresa, preview = false) => {
  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'letter'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const black = [0, 0, 0];
    const headerBg = [200, 210, 230]; // Azul claro
    
    // ============= TÍTULO PRINCIPAL =============
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(margin, 10, pageWidth - margin * 2, 18);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('COMPROBANTE DE RETENCION DE IMPUESTO SOBRE LA RENTA', pageWidth / 2, 17, { align: 'center' });
    doc.text('DECRETO 1808', pageWidth / 2, 23, { align: 'center' });
    
    // ============= FILA IDENTIFICACIÓN =============
    let y = 32;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    
    // Numero de comprobante
    doc.text('NUMERO DE COMPROBANTE: ', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(retention.numero_comprobante || '', margin + 42, y);
    
    // Periodo fiscal y año
    const fechaObj = parseLocalDate(retention.fecha);
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    doc.setFont('helvetica', 'bold');
    doc.text('PERIODO FISCAL: ', pageWidth / 2 - 10, y);
    doc.setFont('helvetica', 'normal');
    doc.text(meses[fechaObj.getMonth()], pageWidth / 2 + 15, y);
    
    doc.setFont('helvetica', 'bold');
    doc.text('AÑO: ', pageWidth / 2 + 40, y);
    doc.setFont('helvetica', 'normal');
    doc.text(fechaObj.getFullYear().toString(), pageWidth / 2 + 50, y);
    
    doc.setFont('helvetica', 'bold');
    doc.text('FECHA ELABORACION: ', pageWidth - margin - 50, y);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDateVE(retention.fecha), pageWidth - margin - 15, y);
    
    // ============= AGENTE DE RETENCIÓN =============
    y += 6;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
    doc.rect(margin, y, pageWidth - margin * 2, 6);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('NOMBRE O RAZON SOCIAL DEL AGENTE DE RETENCION', margin + 2, y + 4);
    
    y += 6;
    doc.rect(margin, y, pageWidth - margin * 2 - 40, 8);
    doc.rect(pageWidth - margin - 40, y, 40, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(empresa?.nombre || '', margin + 2, y + 5);
    doc.text(empresa?.rif || '', pageWidth - margin - 38, y + 5);
    
    // Dirección Agente
    y += 8;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
    doc.rect(margin, y, pageWidth - margin * 2, 6);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('DIRECCION FISCAL DEL AGENTE DE RETENCION', margin + 2, y + 4);
    
    y += 6;
    doc.rect(margin, y, pageWidth - margin * 2, 8);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(empresa?.direccion || '', margin + 2, y + 5);
    
    // ============= CONTRIBUYENTE =============
    y += 10;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
    doc.rect(margin, y, pageWidth - margin * 2, 6);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('NOMBRE O RAZON SOCIAL DEL CONTRIBUYENTE Y RIF O CEDULA DEL CONTRIBUYENTE', margin + 2, y + 4);
    
    y += 6;
    doc.rect(margin, y, pageWidth - margin * 2 - 40, 8);
    doc.rect(pageWidth - margin - 40, y, 40, 8);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(provider?.nombre || '', margin + 2, y + 5);
    doc.text(provider?.rif || '', pageWidth - margin - 38, y + 5);
    
    // Dirección Contribuyente
    y += 8;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 6, 'F');
    doc.rect(margin, y, pageWidth - margin * 2, 6);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('DIRECCION FISCAL DEL CONTRIBUYENTE', margin + 2, y + 4);
    
    y += 6;
    doc.rect(margin, y, pageWidth - margin * 2, 8);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(provider?.direccion || '', margin + 2, y + 5);
    
    // ============= TÍTULO RECIBO =============
    y += 12;
    doc.setFillColor(...headerBg);
    doc.rect(margin, y, pageWidth - margin * 2, 8, 'F');
    doc.rect(margin, y, pageWidth - margin * 2, 8);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('RECIBO DE RETENCION DE IMPUESTO SOBRE LA RENTA, SEGUN DECRETO 1808', pageWidth / 2, y + 5, { align: 'center' });
    
    // ============= TABLA ISLR =============
    y += 12;
    
    // Para ISLR, la retención se calcula sobre la base imponible (subtotal), no sobre el monto total
    const baseImponible = parseFloat(retention.subtotal) || 0;
    const porcentaje = parseFloat(retention.porcentaje_retencion) || 0;
    const montoRetenido = parseFloat(retention.monto_retenido) || 0;
    const totalPagarProveedor = baseImponible - montoRetenido;
    
    const tableData = [[
      formatDateVE(retention.fecha_factura),
      retention.numero_factura || '',
      retention.numero_control || '', // Control Fiscal
      'FACTURA',
      formatCurrency(baseImponible),
      `${porcentaje}%`,
      formatCurrency(montoRetenido),
      '1,00', // Sustraendo (valor fijo común)
      formatCurrency(montoRetenido),
      formatCurrency(totalPagarProveedor)
    ]];
    
    autoTable(doc, {
      startY: y,
      head: [[
        { content: 'DATOS DEL DOCUMENTO', colSpan: 4, styles: { halign: 'center' } },
        { content: '', colSpan: 6 }
      ], [
        'FECHA\nEMISION',
        'NUMERO',
        'CONTROL\nFISCAL',
        'TIPO\nDOCUMENTO',
        'BASE\nIMPONIBLE',
        '% DE\nRETENCION',
        'MONTO\nRETENIDO',
        'SUSTRA-\nENDO',
        'IMPUESTOS\nACUMULADO',
        'TOTAL A PAGAR\nPROVEEDOR'
      ]],
      body: tableData,
      foot: [[
        { content: 'TOTAL GENERAL', colSpan: 4, styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(baseImponible), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: '', styles: { halign: 'center' } },
        { content: formatCurrency(montoRetenido), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: '1,00', styles: { halign: 'right' } },
        { content: formatCurrency(montoRetenido), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(totalPagarProveedor), styles: { fontStyle: 'bold', halign: 'right' } }
      ]],
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, lineColor: black, lineWidth: 0.2 },
      headStyles: { fillColor: headerBg, textColor: black, fontStyle: 'bold', halign: 'center', fontSize: 6 },
      footStyles: { fillColor: [255, 255, 255], textColor: black },
      columnStyles: {
        0: { cellWidth: 22, halign: 'center' },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 24, halign: 'center' },
        4: { cellWidth: 28, halign: 'right' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 26, halign: 'right' },
        7: { cellWidth: 20, halign: 'right' },
        8: { cellWidth: 26, halign: 'right' },
        9: { cellWidth: 30, halign: 'right' },
      },
    });
    
    // ============= TOTAL A PAGAR =============
    const finalY = doc.lastAutoTable?.finalY || y + 40;
    
    doc.rect(margin, finalY + 2, pageWidth - margin * 2, 8);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL A PAGAR PROVEEDOR :', pageWidth - margin - 80, finalY + 7);
    doc.text(formatCurrency(totalPagarProveedor), pageWidth - margin - 5, finalY + 7, { align: 'right' });
    
    // ============= ENTREGADO A Y FIRMAS (en horizontal) =============
    const firmaY = finalY + 20;
    const seccionWidth = (pageWidth - margin * 2) / 4;
    
    // Sección 1: Entregado A
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('ENTREGADO A:', margin, firmaY);
    doc.setFont('helvetica', 'normal');
    doc.text('Apellido Y Nombre:', margin, firmaY + 6);
    doc.line(margin + 28, firmaY + 6, margin + seccionWidth - 5, firmaY + 6);
    doc.text('Cedula:', margin, firmaY + 12);
    doc.line(margin + 15, firmaY + 12, margin + seccionWidth - 5, firmaY + 12);
    
    // Sección 2: Firma Agente
    doc.line(margin + seccionWidth, firmaY + 10, margin + seccionWidth * 2 - 10, firmaY + 10);
    doc.text('Firma Agente de Retención', margin + seccionWidth + (seccionWidth - 10) / 2, firmaY + 14, { align: 'center' });
    
    // Sección 3: Firma Sujeto Retenido
    doc.line(margin + seccionWidth * 2, firmaY + 10, margin + seccionWidth * 3 - 10, firmaY + 10);
    doc.text('Firma Sujeto Retenido', margin + seccionWidth * 2 + (seccionWidth - 10) / 2, firmaY + 14, { align: 'center' });
    
    // Sección 4: Fecha de Recepción
    doc.setFont('helvetica', 'bold');
    doc.text('FECHA DE RECEPCION:', margin + seccionWidth * 3, firmaY);
    doc.setFont('helvetica', 'normal');
    doc.line(margin + seccionWidth * 3, firmaY + 10, pageWidth - margin, firmaY + 10);
    
    // Si es preview, devolver el documento sin guardar
    if (preview) {
      return doc;
    }
    
    // Guardar
    const filename = `Retencion_ISLR_${retention.numero_comprobante}_${provider?.nombre?.replace(/[^a-zA-Z0-9]/g, '_') || 'Sin_Proveedor'}.pdf`;
    doc.save(filename);
    return doc;
    
  } catch (error) {
    console.error('Error generando PDF ISLR:', error);
    throw new Error('Error al generar el PDF: ' + error.message);
  }
};

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};
